// components/ClientVisits.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector } from "react-redux";
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format, parseISO, isWithinInterval } from 'date-fns';
import { FaPlus, FaArrowLeft } from "react-icons/fa";
import { BsFlag, BsFlagFill } from "react-icons/bs";
import {
  MoreHorizontal,
  Eye,
  Edit2,
  Filter,
  X,
  RefreshCw,
  MoreVertical,
  FileSpreadsheet,
  FileText,
  Search,
} from "lucide-react";
import Logo from "../assets/images/Logo.png";
import { FcUp, FcDown  } from "react-icons/fc";
import { exportToExcel, exportToPDF } from '../components/ClientVisitsComponents/exportClientVisits';
import ExportCard from '../helpers/ExportCard';

import Pagination from '../helpers/Pagination';
import PageLoader from '../helpers/PageLoader';
import NoDataAvailable from '../helpers/NoDataAvailable';
import DeleteConfirmationOffcanvas from '../helpers/DeleteConfirmationOffcanvas';
import SelectDropdown from '../customStyles/SelectDropdown';
import CustomDatePicker from '../customStyles/CustomDatePicker';
import api from '../common/apiClient';
import SummaryApi from '../common/SummaryApi';
import { sourceTypeOptions } from '../components/ClientVisitsComponents/IndustryData';

const ClientVisits = () => {
  const { portalName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const exportRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [visits, setVisits] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [menuIndex, setMenuIndex] = useState(null);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    selectedData: null,
    error: null,
  });
  const [sortOrder, setSortOrder] = useState("desc");
  const user = useSelector((state) => state?.user?.user);
  
  const isOwner = useMemo(() => {
    return user?.role === 'owner' || user?.role === 'admin' || user?.isOwner === true;
  }, [user]);

  // Filter states
  const [filters, setFilters] = useState({
    divisionId: '',
    businessVerticalId: '',
    industryType: '',
    stageId: '',
    paymentStatus: '',
    productCategory: '',
    fromDate: '',
    toDate: '',
    userId: '',
    ownerId: '',
    roleId: '',
    priorityFollowUp: '',
    sourceType: '',
    expoPlace: '',
    ourProductId: '',                 
    ourProductBusinessCategoryId: '', 
  });

  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  // Filter options
  const [filterOptions, setFilterOptions] = useState({
    divisions: [],
    businessVerticals: [],
    industries: [],
    stages: [],
    productCategories: [],
    paymentStatuses: [
      { value: 'pending', label: 'Pending' },
      { value: 'partial', label: 'Partial' },
      { value: 'completed', label: 'Completed' },
    ],
    users: [],
    owners: [],
    roles: [],
    sourceTypes: [],
    expoPlaces: [],
    ourProducts: [], 
    ourBusinessCategories: [],
    programs: [],
  });

  // Pagination states
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [exportCard, setExportCard] = useState({
    isOpen: false,
    type: null,
    isLoading: false,
    exportProgress: 0,
    status: 'idle',
    message: '',
    autoClose: true,
    autoCloseDelay: 3000
  });
  const [reportData, setReportData] = useState([]);

  // Fetch Executive Owners from API & Merge with Visits Data
  const fetchExecutives = useCallback(async (visitsData = []) => {
    try {
      let formattedList = [];

      try {
        const response = await api.get(SummaryApi.executiveUsers.url);
        const resData = response.data;
        
        let execs = [];
        if (Array.isArray(resData?.data)) execs = resData.data;
        else if (Array.isArray(resData?.users)) execs = resData.users;
        else if (Array.isArray(resData?.executives)) execs = resData.executives;
        else if (Array.isArray(resData)) execs = resData;

        formattedList = execs.map((exec) => {
          const rawId = exec._id || exec.id || exec.userId || '';
          const nameStr = (
            exec.fullName
              ? `${exec.fullName} ${exec.lastName || ''}`.trim()
              : `${exec.firstName || ''} ${exec.lastName || ''}`.trim() || exec.clientName || exec.email || ''
          ) || 'Executive';

          return {
            value: String(rawId),
            label: nameStr
          };
        });
      } catch (err) {
        console.log("Fallback to visitsData for owners...");
      }

      // Merge owners extracted directly from visitsData
      if (Array.isArray(visitsData) && visitsData.length > 0) {
        visitsData.forEach((v) => {
          if (v.ownerId) {
            const rawId = typeof v.ownerId === 'object' ? v.ownerId._id : v.ownerId;
            if (rawId) {
              const exists = formattedList.some((o) => o.value === String(rawId));
              if (!exists) {
                const ownerObj = typeof v.ownerId === 'object' ? v.ownerId : null;
                const ownerName = ownerObj
                  ? `${ownerObj.firstName || ''} ${ownerObj.lastName || ''} (${ownerObj.userId || ''})`.trim()
                  : String(rawId);
                formattedList.push({
                  value: String(rawId),
                  label: ownerName
                });
              }
            }
          }
        });
      }

      setFilterOptions((prev) => ({
        ...prev,
        owners: formattedList
      }));
    } catch (error) {
      console.error("Error fetching executives for filter:", error);
    }
  }, []);

  // Checks if the filtered division is CUSO
  const isCusoDivision = Boolean(
    filterOptions.divisions.find(d => d.value === filters.divisionId)?.label?.toLowerCase().includes('cuso')
  );

// Fetch Business Verticals (Strictly isVertical === true and division-filtered)
  const fetchBusinessVerticals = useCallback(async (selectedDivisionId = null) => {
    try {
      const params = { isActive: "true" };
      if (selectedDivisionId) {
        params.divisionId = selectedDivisionId;
      }

      const response = await api.get(SummaryApi.getAllProducts.url, { params });
      if (response.data?.success) {
        const allItems = response.data.data || [];
        
        const verticals = allItems
          .filter(item => item.isVertical === true)
          .map((v) => ({
            value: v._id,
            label: v.name,
          }));

        setFilterOptions((prev) => ({
          ...prev,
          businessVerticals: verticals,
        }));
      }
    } catch (error) {
      console.error("Error fetching business verticals filter:", error);
    }
  }, []);

  useEffect(() => {
    fetchBusinessVerticals(filters.divisionId || null);
  }, [filters.divisionId, fetchBusinessVerticals]);


  const fetchFilterPrograms = useCallback(async (selectedDivisionId = null) => {
    try {
      const params = { isActive: "true" };
      if (selectedDivisionId) {
        params.divisionId = selectedDivisionId;
      }

      const response = await api.get(SummaryApi.getAllPrograms.url, { params });
      if (response.data?.success) {
        const formatted = (response.data.data || []).map((p) => ({
          value: p._id,
          label: p.name,
        }));

        setFilterOptions((prev) => ({
          ...prev,
          programs: formatted,
        }));
      }
    } catch (error) {
      console.error("Error fetching programs filter:", error);
    }
  }, []);

  useEffect(() => {
    fetchFilterPrograms(filters.divisionId || null);
  }, [filters.divisionId, fetchFilterPrograms]);

  // 1. Fetch Our ERP Products using SummaryApi when CUSO division is active
  const fetchOurProducts = useCallback(async () => {
      try {
        const response = await api.get(SummaryApi.getAllProducts.url, {
          params: { isActive: "true" }
        });

        if (response.data?.success) {
          const formatted = (response.data.data || [])
            .filter((p) => !p.isVertical)
            .map((p) => ({
              value: p._id,
              label: p.name,
            }));

          setFilterOptions((prev) => ({
            ...prev,
            ourProducts: formatted,
          }));
        }
      } catch (error) {
        console.error("Error fetching our products filter:", error);
      }
    }, []);

  // Call when CUSO division is selected
  useEffect(() => {
    if (isCusoDivision && filterOptions.ourProducts.length === 0) {
      fetchOurProducts();
    }
  }, [isCusoDivision, filterOptions.ourProducts.length, fetchOurProducts]);

// Fetch Stages dynamically based on Division and Business Vertical
  const fetchFilterStages = useCallback(async (selectedDivisionId = null, selectedVerticalId = null) => {
    try {
      const params = {
        includeInactive: false,
        includeShared: true, // Shared stages-ம் கூடவே வரும்
      };

      if (selectedDivisionId) params.divisionId = selectedDivisionId;
      if (selectedVerticalId) params.verticalId = selectedVerticalId;

      const response = await api.get(SummaryApi.getAllStages.url, { params });
      if (response.data?.success) {
        const formatted = (response.data.data || []).map((s) => ({
          value: s._id,
          label: s.stageName,
        }));

        setFilterOptions((prev) => ({
          ...prev,
          stages: formatted,
        }));
      }
    } catch (error) {
      console.error("Error fetching filter stages:", error);
    }
  }, []);

  useEffect(() => {
    fetchFilterStages(
      filters.divisionId || null, 
      filters.businessVerticalId || null
    );
  }, [filters.divisionId, filters.businessVerticalId, fetchFilterStages]);

  // 2. Fetch Business Categories using SummaryApi based on Selected Product
  const fetchBusinessCategories = useCallback(async (productId) => {
    if (!productId) {
      setFilterOptions((prev) => ({ ...prev, ourBusinessCategories: [] }));
      return;
    }

    try {
      const response = await api.get(SummaryApi.getAllBusinessCategories.url, {
        params: { productId, isActive: "true" }
      });

      if (response.data?.success) {
        const formatted = (response.data.data || []).map((c) => ({
          value: c._id, // 👈 ClientVisits.jsx uses "value" key
          label: c.name,
        }));

        setFilterOptions((prev) => ({
          ...prev,
          ourBusinessCategories: formatted,
        }));
      }
    } catch (error) {
      console.error("Error fetching business categories filter:", error);
    }
  }, []);

  // Fetch categories whenever selected product filter changes
  useEffect(() => {
    if (filters.ourProductId) {
      fetchBusinessCategories(filters.ourProductId);
    } else {
      setFilterOptions((prev) => ({ ...prev, ourBusinessCategories: [] }));
    }
  }, [filters.ourProductId, fetchBusinessCategories]);
  
  const buildQueryParams = () => {
    const params = new URLSearchParams();

    if (filters.fromDate) params.append('fromDate', filters.fromDate);
    if (filters.toDate) params.append('toDate', filters.toDate);
    if (filters.divisionId) params.append('divisionId', filters.divisionId);
    if (filters.industryType) params.append('industryType', filters.industryType);
    if (filters.stageId) params.append('stageId', filters.stageId);
    if (filters.paymentStatus) params.append('paymentStatus', filters.paymentStatus);
    if (filters.productCategory) params.append('productCategory', filters.productCategory);
    if (filters.userId) params.append('userId', filters.userId);
    if (filters.ownerId) params.append('ownerId', filters.ownerId);
    if (filters.roleId) params.append('roleId', filters.roleId);
    if (filters.sourceType) params.append('sourceType', filters.sourceType);
    if (filters.expoPlace) params.append('expoPlace', filters.expoPlace);
    if (filters.ourProductId) params.append('ourProductId', filters.ourProductId);
    if (filters.ourProductBusinessCategoryId) params.append('ourProductBusinessCategoryId', filters.ourProductBusinessCategoryId);
    if (filters.programId) params.append('programId', filters.programId); // ✅
    if (filters.businessVerticalId) params.append('businessVerticalId', filters.businessVerticalId);
    return params.toString();
  };

  const handleExport = async (exportType) => {
    setIsMoreOpen(false);

    setExportCard({
      isOpen: true,
      type: exportType,
      isLoading: true,
      exportProgress: 0,
      status: 'loading',
      message: `Preparing ${exportType.toUpperCase()} export...`,
      autoClose: true,
      autoCloseDelay: 3000
    });

    try {
      setExportCard(prev => ({ ...prev, exportProgress: 30 }));

      const queryString = buildQueryParams();
      const response = await api.get(`${SummaryApi.getAllClientVisitsReport.url}?${queryString}`);

      setExportCard(prev => ({ ...prev, exportProgress: 70 }));

      if (response.data.success) {
        setReportData(response.data.data);
        setExportCard(prev => ({
          ...prev,
          exportProgress: 100,
          status: 'success',
          message: `${exportType.toUpperCase()} file is ready for download!`,
          isLoading: false
        }));
      }
    } catch (error) {
      console.error('Export failed:', error);
      setExportCard(prev => ({
        ...prev,
        status: 'error',
        message: `Failed to export ${exportType.toUpperCase()}. Please try again.`,
        isLoading: false,
        exportProgress: 0
      }));
      toast.error('Failed to export data');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setMenuIndex(null);
      }
      if (exportRef.current && !exportRef.current.contains(event.target)) {
        setIsMoreOpen(false);
      }
    };

    const handleScroll = () => {
      setMenuIndex(null);
      setIsMoreOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  const extractFilterOptions = useCallback((visitsData) => {
    const divisions = [...new Set(visitsData.map(v => v.divisionId?._id).filter(Boolean))];
    const industries = [...new Set(visitsData.map(v => v.industryType).filter(Boolean))];
    const stages = [...new Set(visitsData.map(v => v.stageId?._id).filter(Boolean))];
    const categories = [...new Set(visitsData.flatMap(v => v.productCategory || []))];
    const users = [...new Set(visitsData.map(v => v.userId?._id).filter(Boolean))];
    
    const roles = [...new Set(visitsData.map(v => {
      if (typeof v.userId === 'object') {
        return v.userId.customRoleId?._id || v.userId.role;
      }
      return null;
    }).filter(Boolean))];

    const allSourceTypes = sourceTypeOptions.map(st => ({
      value: st.id,
      label: st.label
    }));

    const dataSources = [...new Set(visitsData.map(v => v.sourceType).filter(Boolean))];
    dataSources.forEach(stId => {
      const exists = allSourceTypes.some(opt => opt.value.toLowerCase() === stId.toLowerCase());
      if (!exists) {
        const formattedLabel = stId
          .replace(/[_]/g, ' ')
          .replace(/\b\w/g, char => char.toUpperCase());

        allSourceTypes.push({
          value: stId,
          label: formattedLabel
        });
      }
    });

    const expoPlaces = [...new Set(visitsData.map(v => v.expoPlace).filter(Boolean))];

    setFilterOptions(prev => ({
      ...prev,
      divisions: [
        ...divisions.map(id => {
          const division = visitsData.find(v => v.divisionId?._id === id)?.divisionId;
          return { value: id, label: division?.name || id };
        })
      ],
      industries: [
        ...industries.map(ind => ({ value: ind, label: ind }))
      ],
      stages: [
        ...stages.map(id => {
          const stage = visitsData.find(v => v.stageId?._id === id)?.stageId;
          return { value: id, label: stage?.stageName || id };
        })
      ],
      productCategories: [
        ...categories.map(cat => ({ value: cat, label: cat }))
      ],
      users: [
        ...users.map(id => {
          const user = visitsData.find(v => v.userId?._id === id)?.userId;
          const userName = user ? `${user.firstName || ''} ${user.lastName || ''} (${user.userId || ''})`.trim() : id;
          return { value: id, label: userName };
        })
      ],
      roles: [
        ...roles.map(roleId => {
          const visit = visitsData.find(v => {
            if (typeof v.userId === 'object') {
              return (v.userId.customRoleId?._id === roleId || v.userId.role === roleId);
            }
            return false;
          });
          const roleName = visit?.userId?.customRoleId?.roleName ||
            (visit?.userId?.role === 'owner' ? 'Owner' : roleId);
          return { value: roleId, label: roleName };
        })
      ],
      sourceTypes: allSourceTypes,
      expoPlaces: [
        ...expoPlaces.map(ep => ({ value: ep, label: ep }))
      ]
    }));

    // Fetch Executive Owners & map from visitsData
    fetchExecutives(visitsData);
  }, [fetchExecutives]);

  const fetchVisits = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(SummaryApi.getAllClientVisits.url,{
      params: {
        isDeleted: false,
      },
    });

      if (response.data.success) {
        const visitsData = response.data.data || [];
        setVisits(visitsData);
        extractFilterOptions(visitsData);
      }
    } catch (err) {
      setError('Failed to fetch visits');
      console.error(err);
      toast.error('Failed to fetch client visits');
    } finally {
      setLoading(false);
    }
  }, [extractFilterOptions]);

  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  useEffect(() => {
    let result = [...visits];

    result.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);

      if (sortOrder === "asc") {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      result = result.filter(visit => 
        (visit.clientName && visit.clientName.toLowerCase().includes(searchLower)) ||
        (visit.contactName && visit.contactName.toLowerCase().includes(searchLower)) ||
        (visit.contactPhone && visit.contactPhone.includes(searchTerm.trim())) ||
        (visit.contactLandline && visit.contactLandline.includes(searchTerm.trim()))
      );
    }

    if (filters.fromDate || filters.toDate) {
      result = result.filter(visit => {
        const visitDate = parseISO(visit.createdAt);

        const fromDate = filters.fromDate
          ? new Date(filters.fromDate + 'T00:00:00')
          : new Date('1900-01-01');

        const toDate = filters.toDate
          ? new Date(filters.toDate + 'T23:59:59.999')
          : new Date('2100-12-31');

        return isWithinInterval(visitDate, { start: fromDate, end: toDate });
      });
    }

    if (filters.divisionId) {
      result = result.filter(v => v.divisionId?._id === filters.divisionId);
    }

    if (filters.industryType) {
      result = result.filter(v => v.industryType === filters.industryType);
    }

    if (filters.stageId) {
      result = result.filter(v => v.stageId?._id === filters.stageId);
    }

    if (filters.paymentStatus) {
      result = result.filter(v => v.paymentStatus === filters.paymentStatus);
    }

    if (filters.productCategory) {
      result = result.filter(v => v.productCategory?.includes(filters.productCategory));
    }

    if (filters.userId) {
      result = result.filter(v => v.userId?._id === filters.userId);
    }

    // Filter by Owner ID
    if (filters.ownerId) {
      result = result.filter(v => {
        if (!v.ownerId) return false;
        const oId = typeof v.ownerId === 'object' ? v.ownerId._id : v.ownerId;
        return String(oId) === String(filters.ownerId);
      });
    }
    // Filter by Business Vertical
    if (filters.businessVerticalId) {
      result = result.filter(v => {
        const vertId = typeof v.businessVerticalId === 'object' ? v.businessVerticalId?._id : v.businessVerticalId;
        const prodId = typeof v.ourProductId === 'object' ? v.ourProductId?._id : v.ourProductId;
        return String(vertId) === String(filters.businessVerticalId) || String(prodId) === String(filters.businessVerticalId);
      });
    }
    if (filters.roleId) {
      result = result.filter(v => {
        if (typeof v.userId === 'object') {
          return v.userId.customRoleId?._id === filters.roleId || v.userId.role === filters.roleId;
        }
        return false;
      });
    }

    if (filters.priorityFollowUp) {
      result = result.filter((v) => v.isPriorityFollowUp === true);
    }

    if (filters.sourceType) {
      result = result.filter(v => v.sourceType && v.sourceType.toLowerCase() === filters.sourceType.toLowerCase());
    }

    if (filters.sourceType?.toLowerCase() === 'expo' && filters.expoPlace) {
      result = result.filter(v => v.expoPlace && v.expoPlace.toLowerCase() === filters.expoPlace.toLowerCase());
    }
   // Filter by Our ERP Product
    if (filters.ourProductId) {
      result = result.filter(v => {
        const pId = typeof v.ourProductId === 'object' ? v.ourProductId?._id : v.ourProductId;
        return String(pId) === String(filters.ourProductId);
      });
    }

    // Filter by Product Business Category
    if (filters.ourProductBusinessCategoryId) {
      result = result.filter(v => {
        const cId = typeof v.ourProductBusinessCategoryId === 'object' ? v.ourProductBusinessCategoryId?._id : v.ourProductBusinessCategoryId;
        return String(cId) === String(filters.ourProductBusinessCategoryId);
      });
    }
    if (filters.programId) {
      result = result.filter(v => {
        const pId = typeof v.programId === 'object' ? v.programId?._id : v.programId;
        return String(pId) === String(filters.programId);
      });
    }
    setFilteredData(result);
    setCurrentPage(1);
  }, [visits, filters, searchTerm, sortOrder]);

  const toggleSort = () => {
    setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));
  };

  useEffect(() => {
    if (!isInitialized) return;

    const params = new URLSearchParams();

    if (searchTerm) params.set("search", searchTerm);

    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      }
    });

    navigate(`?${params.toString()}`, { replace: true });
  }, [filters, searchTerm, navigate, isInitialized]);

  useEffect(() => {
    if (isInitialized) return;

    const params = new URLSearchParams(location.search);

    const newFilters = {
      divisionId: params.get("divisionId") || "",
      industryType: params.get("industryType") || "",
      stageId: params.get("stageId") || "",
      paymentStatus: params.get("paymentStatus") || "",
      productCategory: params.get("productCategory") || "",
      fromDate: params.get("fromDate") || "",
      toDate: params.get("toDate") || "",
      userId: params.get("userId") || "",
      ownerId: params.get("ownerId") || "",
      roleId: params.get("roleId") || "",
      sourceType: params.get("sourceType") || "",
      expoPlace: params.get("expoPlace") || "",
      ourProductId: params.get("ourProductId") || "",
      ourProductBusinessCategoryId: params.get("ourProductBusinessCategoryId") || "",
    };

    setFilters(newFilters);
    setSearchTerm(params.get("search") || "");

    setIsInitialized(true);
  }, [location.search, isInitialized]);

  useEffect(() => {
    let count = 0;
    if (searchTerm.trim()) count++;
    if (filters.fromDate || filters.toDate) count++;
    if (filters.divisionId) count++;
    if (filters.industryType) count++;
    if (filters.stageId) count++;
    if (filters.paymentStatus) count++;
    if (filters.productCategory) count++;
    if (filters.userId) count++;
    if (filters.ownerId) count++;
    if (filters.roleId) count++;
    if (filters.priorityFollowUp) count++;
    if (filters.sourceType) count++;
    if (filters.sourceType?.toLowerCase() === 'expo' && filters.expoPlace) count++;
    if (filters.ourProductId) count++;
    if (filters.ourProductBusinessCategoryId) count++;
    if (filters.programId) count++;
    if (filters.businessVerticalId) count++;
    setActiveFiltersCount(count);
  }, [filters, searchTerm]);

  const setDatePreset = (preset) => {
    const today = new Date();
    let fromDate = today;
    let toDate = today;

    switch (preset) {
      case 'today':
        fromDate = today;
        toDate = today;
        break;
      case 'yesterday':
        fromDate = new Date(today.setDate(today.getDate() - 1));
        toDate = fromDate;
        break;
      case 'last7days':
        fromDate = new Date(today.setDate(today.getDate() - 7));
        toDate = new Date();
        break;
      case 'last30days':
        fromDate = new Date(today.setDate(today.getDate() - 30));
        toDate = new Date();
        break;
      default:
        break;
    }

    setFilters(prev => ({
      ...prev,
      fromDate: format(fromDate, 'yyyy-MM-dd'),
      toDate: format(toDate, 'yyyy-MM-dd')
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      divisionId: '',
      industryType: '',
      stageId: '',
      paymentStatus: '',
      productCategory: '',
      fromDate: '',
      toDate: '',
      userId: '',
      ownerId: '',
      roleId: '',
      priorityFollowUp: '',
      sourceType: '',
      expoPlace: '',
      ourProductId: '',
      ourProductBusinessCategoryId: '',
      programId: '',
      businessVerticalId: '',
    });
    setSearchTerm('');
  };

  const clearFilter = (filterKey) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: ''
    }));
  };

  const handleDelete = async (id) => {
    try {
      const response = await api.delete(SummaryApi.deleteClientVisit.url(id));
      if (response.data.success) {
        setVisits((prev) => prev.filter((visit) => visit._id !== id));
        toast.success('Visit deleted successfully');
        setDeleteConfirmation({
          isOpen: false,
          selectedData: null,
          error: null,
        });
      }
    } catch (err) {
      console.error('Failed to delete visit:', err);
      toast.error(err.response?.data?.message || 'Error deleting visit');
    }
  };

  const handleView = (visit) => {
    navigate(`/${portalName}/sales/leads/view/${visit._id}`, {
      state: {
        allIds: filteredData.map(i => i._id)
      }
    });
    setMenuIndex(null);
  };

  const handleEdit = (visit) => {
    navigate(`/${portalName}/sales/leads/edit/${visit._id}`);
    setMenuIndex(null);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      'pending': 'bg-red-100 text-red-800',
      'partial': 'bg-yellow-100 text-yellow-800',
      'completed': 'bg-green-100 text-green-800',
      'na': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const totalItems = filteredData.length;
  const paginatedData = itemsPerPage === "All"
    ? filteredData
    : filteredData.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );

  let serialNumber = 1;

  return (
    <div className="w-full p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <button
            onClick={handleBackClick}
            className="mr-2 hover:bg-gray-300 rounded-full p-1"
          >
            <FaArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold">All Leads</h1>
            {activeFiltersCount > 0 && (
              <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full">
                {activeFiltersCount} filters
              </span>
            )}
          </div>
        </div>
        <div className="relative mt-2 md:mt-0 md:ml-4 w-full md:w-64">
          <input
            type="text"
            placeholder="Search client, contact or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg transition-colors ${
              showFilters
                ? "bg-red-100 text-red-700"
                : "bg-gray-100 hover:bg-gray-200"
            }`}
            title={showFilters ? "Hide Filters" : "Show Filters"}
          >
            <Filter className="w-5 h-5" />
          </button>

          <div className="relative inline-block text-left" ref={exportRef}>
            <button
              onClick={() => setIsMoreOpen(!isMoreOpen)}
              className="p-2 rounded-full hover:bg-gray-200 transition"
            >
              <MoreVertical className="w-5 h-5 text-gray-700" />
            </button>
            {isMoreOpen && (
              <div className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5">
                <div className="py-1">
                  <button
                    onClick={() => isOwner && handleExport("excel")}
                    disabled={!isOwner}
                    className={`w-full flex items-center justify-between px-4 py-2 text-sm ${
                      isOwner
                        ? "text-gray-700 hover:bg-gray-100 cursor-pointer"
                        : "text-gray-400 cursor-not-allowed opacity-60"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <FileSpreadsheet className="w-4 h-4" />
                      <span>Export to Excel</span>
                    </div>
                  </button>

                  <button
                    onClick={() => isOwner && handleExport("pdf")}
                    disabled={!isOwner}
                    className={`w-full flex items-center justify-between px-4 py-2 text-sm ${
                      isOwner
                        ? "text-gray-700 hover:bg-gray-100 cursor-pointer"
                        : "text-gray-400 cursor-not-allowed opacity-60"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <FileText className="w-4 h-4" />
                      <span>Export to PDF</span>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>

          <Link
            to={`/${portalName}/sales/leads/create`}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <FaPlus /> New Lead
          </Link>
        </div>
      </div>

      {/* Filters Section */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => setDatePreset("today")}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Today
            </button>
            <button
              onClick={() => setDatePreset("yesterday")}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Yesterday
            </button>
            <button
              onClick={() => setDatePreset("last7days")}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Last 7 Days
            </button>
            <button
              onClick={() => setDatePreset("last30days")}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Last 30 Days
            </button>
          </div>

          {/* Filter Controls Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <CustomDatePicker
                value={filters.fromDate}
                onChange={(date) =>
                  setFilters((prev) => {
                    const next = { ...prev, fromDate: date };
                    if (!prev.toDate && date) {
                      next.toDate = date;
                    }
                    return next;
                  })
                }
                placeholder="Select date"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <CustomDatePicker
                value={filters.toDate}
                onChange={(date) =>
                  setFilters((prev) => {
                    const next = { ...prev, toDate: date };
                    if (!prev.fromDate && date) {
                      next.fromDate = date;
                    }
                    return next;
                  })
                }
                placeholder="Select date"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Division
              </label>
              <SelectDropdown
                options={filterOptions.divisions}
                value={filters.divisionId}
                onChange={(value) => {
                  const isCuso = filterOptions.divisions.find(d => d.value === value)?.label?.toLowerCase().includes('cuso');
                  setFilters((prev) => ({
                    ...prev,
                    divisionId: value,
                    businessVerticalId: '',
                    stageId: '',
                    ...(!isCuso && { ourProductId: '', ourProductBusinessCategoryId: '' })
                  }));
                }}
                placeholder="All Divisions"
                valueKey="value"
                labelKey="label"
              />
            </div>
            {/* Business Vertical Filter (Common for all divisions) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Vertical
              </label>
              <SelectDropdown
                options={filterOptions.businessVerticals}
                value={filters.businessVerticalId}
                onChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    businessVerticalId: value,
                    stageId: '', // Reset stage when vertical changes
                  }))
                }
                placeholder={filters.divisionId ? "All Verticals" : "Select Division First"}
                valueKey="value"
                labelKey="label"
                disabled={!filters.divisionId}
                searchable={true}
              />
            </div>

            {/* Render ERP Product & Category ONLY when Division is CUSO */}
            {isCusoDivision && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ERP Product
                  </label>
                  <SelectDropdown
                    options={filterOptions.ourProducts}
                    value={filters.ourProductId}
                    onChange={(value) =>
                      setFilters((prev) => ({
                        ...prev,
                        ourProductId: value,
                        ourProductBusinessCategoryId: '' // Reset category when product changes
                      }))
                    }
                    placeholder="All ERP Products"
                    valueKey="value"
                    labelKey="label"
                    searchable={true}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Category
                  </label>
                  <SelectDropdown
                    options={filterOptions.ourBusinessCategories}
                    value={filters.ourProductBusinessCategoryId}
                    onChange={(value) =>
                      setFilters((prev) => ({ ...prev, ourProductBusinessCategoryId: value }))
                    }
                    placeholder={filters.ourProductId ? "All Categories" : "Select Product First"}
                    valueKey="value"
                    labelKey="label"
                    disabled={!filters.ourProductId}
                    searchable={true}
                  />
                </div>
              </>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Industry
              </label>
              <SelectDropdown
                options={filterOptions.industries}
                value={filters.industryType}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, industryType: value }))
                }
                placeholder="All Industries"
                valueKey="value"
                labelKey="label"
              />
            </div>

            {/* Source Filter using sourceTypeOptions from IndustryData */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Source
              </label>
              <SelectDropdown
                options={filterOptions.sourceTypes}
                value={filters.sourceType}
                onChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    sourceType: value,
                    expoPlace: value?.toLowerCase() === 'expo' ? prev.expoPlace : ''
                  }))
                }
                placeholder="All Sources"
                valueKey="value"
                labelKey="label"
                searchable={true}
              />
            </div>

            {/* Expo Name Filter (Visible only when Source === 'expo') */}
            {filters.sourceType?.toLowerCase() === 'expo' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expo Name
                </label>
                <SelectDropdown
                  options={filterOptions.expoPlaces}
                  value={filters.expoPlace}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, expoPlace: value }))
                  }
                  placeholder="All Expos"
                  valueKey="value"
                  labelKey="label"
                  searchable={true}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Stage
              </label>
              <SelectDropdown
                options={filterOptions.stages}
                value={filters.stageId}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, stageId: value }))
                }
                placeholder="All Stages"
                valueKey="value"
                labelKey="label"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Status
              </label>
              <SelectDropdown
                options={filterOptions.paymentStatuses}
                value={filters.paymentStatus}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, paymentStatus: value }))
                }
                placeholder="All Payment Status"
                valueKey="value"
                labelKey="label"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Category
              </label>
              <SelectDropdown
                options={filterOptions.productCategories}
                value={filters.productCategory}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, productCategory: value }))
                }
                placeholder="All Categories"
                valueKey="value"
                labelKey="label"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Program / Offering
              </label>
              <SelectDropdown
                options={filterOptions.programs}
                value={filters.programId}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, programId: value }))
                }
                placeholder={filters.divisionId ? "Filter by Program" : "All Programs"}
                valueKey="value"
                labelKey="label"
                searchable={true}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Created By
              </label>
              <SelectDropdown
                options={filterOptions.users}
                value={filters.userId}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, userId: value }))
                }
                placeholder="All Users"
                valueKey="value"
                labelKey="label"
                searchable={true}
              />
            </div>

            {/* Assigned Owner Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assigned Owner
              </label>
              <SelectDropdown
                options={filterOptions.owners}
                value={filters.ownerId}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, ownerId: value }))
                }
                placeholder="All Owners"
                valueKey="value"
                labelKey="label"
                searchable={true}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <SelectDropdown
                options={filterOptions.roles}
                value={filters.roleId}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, roleId: value }))
                }
                placeholder="All Roles"
                valueKey="value"
                labelKey="label"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <SelectDropdown
                options={[
                  {
                    value: "true",
                    label: "Priority Follow-up",
                  },
                ]}
                value={filters.priorityFollowUp}
                onChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    priorityFollowUp: value,
                  }))
                }
                placeholder="All"
                valueKey="value"
                labelKey="label"
              />
            </div>
          </div>

          {/* Active Filters Badges */}
          {activeFiltersCount > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-600">Active filters:</span>

              {searchTerm.trim() && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                  Search: {searchTerm}
                  <button
                    onClick={() => setSearchTerm("")}
                    className="ml-1 hover:text-red-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {(filters.fromDate || filters.toDate) && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                  {filters.fromDate && filters.toDate
                    ? `${format(parseISO(filters.fromDate), "dd MMM")} - ${format(parseISO(filters.toDate), "dd MMM")}`
                    : filters.fromDate
                      ? `From ${format(parseISO(filters.fromDate), "dd MMM yyyy")}`
                      : `To ${format(parseISO(filters.toDate), "dd MMM yyyy")}`}
                  <button
                    onClick={() => {
                      clearFilter("fromDate");
                      clearFilter("toDate");
                    }}
                    className="ml-1 hover:text-red-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {filters.businessVerticalId && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                  Vertical: {filterOptions.businessVerticals.find(v => v.value === filters.businessVerticalId)?.label || "Selected"}
                  <button
                    onClick={() => clearFilter("businessVerticalId")}
                    className="ml-1 hover:text-red-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {filters.divisionId && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                  Division:{" "}
                  {
                    filterOptions.divisions.find(
                      (d) => d.value === filters.divisionId,
                    )?.label
                  }
                  <button
                    onClick={() => clearFilter("divisionId")}
                    className="ml-1 hover:text-red-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {filters.ourProductId && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                  Product: {filterOptions.ourProducts.find(p => p.value === filters.ourProductId)?.label || "Selected"}
                  <button
                    onClick={() => {
                      clearFilter("ourProductId");
                      clearFilter("ourProductBusinessCategoryId");
                    }}
                    className="ml-1 hover:text-red-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {filters.programId && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                  Program: {filterOptions.programs.find(p => p.value === filters.programId)?.label || "Selected"}
                  <button
                    onClick={() => clearFilter("programId")}
                    className="ml-1 hover:text-red-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {filters.ourProductBusinessCategoryId && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                  Category: {filterOptions.ourBusinessCategories.find(c => c.value === filters.ourProductBusinessCategoryId)?.label || "Selected"}
                  <button
                    onClick={() => clearFilter("ourProductBusinessCategoryId")}
                    className="ml-1 hover:text-red-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {filters.industryType && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                  Industry: {filters.industryType}
                  <button
                    onClick={() => clearFilter("industryType")}
                    className="ml-1 hover:text-red-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}

              {filters.sourceType && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                  Source: {filterOptions.sourceTypes.find(s => s.value.toLowerCase() === filters.sourceType.toLowerCase())?.label || filters.sourceType}
                  <button
                    onClick={() => {
                      clearFilter("sourceType");
                      clearFilter("expoPlace");
                    }}
                    className="ml-1 hover:text-red-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}

              {filters.sourceType?.toLowerCase() === 'expo' && filters.expoPlace && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                  Expo Name: {filters.expoPlace}
                  <button
                    onClick={() => clearFilter("expoPlace")}
                    className="ml-1 hover:text-red-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}

              {filters.stageId && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                  Stage:{" "}
                  {
                    filterOptions.stages.find(
                      (s) => s.value === filters.stageId,
                    )?.label
                  }
                  <button
                    onClick={() => clearFilter("stageId")}
                    className="ml-1 hover:text-red-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}

              {filters.paymentStatus && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                  Payment: {filters.paymentStatus}
                  <button
                    onClick={() => clearFilter("paymentStatus")}
                    className="ml-1 hover:text-red-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}

              {filters.productCategory && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                  Category: {filters.productCategory}
                  <button
                    onClick={() => clearFilter("productCategory")}
                    className="ml-1 hover:text-red-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}

              {filters.userId && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                  Created By:{" "}
                  {
                    filterOptions.users.find((u) => u.value === filters.userId)
                      ?.label
                  }
                  <button
                    onClick={() => clearFilter("userId")}
                    className="ml-1 hover:text-red-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}

              {filters.ownerId && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                  Owner:{" "}
                  {
                    filterOptions.owners.find((o) => o.value === filters.ownerId)
                      ?.label
                  }
                  <button
                    onClick={() => clearFilter("ownerId")}
                    className="ml-1 hover:text-red-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}

              {filters.roleId && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                  Role:{" "}
                  {
                    filterOptions.roles.find((r) => r.value === filters.roleId)
                      ?.label
                  }
                  <button
                    onClick={() => clearFilter("roleId")}
                    className="ml-1 hover:text-red-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}

              {filters.priorityFollowUp && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                  🚩 Priority
                  <button
                    onClick={() => clearFilter("priorityFollowUp")}
                    className="ml-1 hover:text-red-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              <button
                onClick={clearAllFilters}
                className="ml-auto text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
              >
                <RefreshCw className="w-3 h-3" />
                Clear All
              </button>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <PageLoader />
      ) : paginatedData.length === 0 ? (
        <NoDataAvailable
          message={
            activeFiltersCount > 0
              ? "No visits found matching your filters"
              : "No client visits found"
          }
          actionLabel="Create New Visit"
          onAction={() =>
            navigate(`/${portalName}/sales/leads/create`)
          }
        />
      ) : (
        <div className="overflow-x-auto custom-scrollbar rounded-md">
          <table className="min-w-full bg-white border-separate border-spacing-y-2 px-2">
            <thead className="text-sm text-gray-600">
              <tr className="text-center rounded-lg shadow-sm bg-red-100">
                <th className="py-4 px-4 rounded-l-lg">S.No</th>
                <th className="py-4 px-4 whitespace-nowrap">Created By</th>
                <th className="py-4 px-4 whitespace-nowrap">Assigned Owner</th>
                <th className="py-4 px-4">Priority</th>               
                <th className="py-4 px-4">Role</th>
                <th className="py-4 px-4 whitespace-nowrap">Client ID</th>
                <th className="py-4 px-4">Division</th>
                <th className="py-4 px-4">Client</th>
                {/* <th className="py-4 px-4">Industry</th> */}
                <th className="py-4 px-4 whitespace-nowrap">
                  <div className="flex flex-col items-center">
                    <span>Value</span>
                    <span className="text-xs text-gray-500 font-normal">
                      [Quotation - Invoiced]
                    </span>
                  </div>
                </th>
                <th className="py-4 px-4">Stage</th>
                <th className="py-4 px-4">Payment</th>
                <th
                  className="py-4 px-4 whitespace-nowrap cursor-pointer"
                  onClick={toggleSort}
                >
                  <div className="flex items-center justify-center">
                    Created Date
                    {sortOrder === "asc" ? (
                      <FcUp className="text-blue-300 w-4 h-4" />
                    ) : (
                      <FcDown className="text-blue-300 w-4 h-4" />
                    )}
                  </div>
                </th>
                <th className="py-4 px-4 whitespace-nowrap">Visit Date</th>
                <th className="py-4 px-4 rounded-r-lg">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((visit, index) => (
                <tr
                  key={visit._id}
                  className="bg-gray-50 hover:bg-gray-100 text-sm rounded-lg shadow-sm text-center"
                >
                  <td className="py-3 px-4 font-medium text-gray-700 rounded-l-lg">
                    {itemsPerPage !== "All"
                      ? currentPage * itemsPerPage -
                        itemsPerPage +
                        serialNumber++
                      : serialNumber++}
                  </td>
                  <td className="py-3 px-4">
                    <div className="min-w-[120px]">
                      <div className="font-medium text-gray-800 truncate">
                        {visit.userId?.firstName} {visit.userId?.lastName}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {visit.userId?.userId || "N/A"}
                      </div>
                    </div>
                  </td>

                  {/* Assigned Owner Column (Right next to Created By) */}
                  <td className="py-3 px-4">
                    <div className="min-w-[120px]">
                      {typeof visit.ownerId === 'object' && visit.ownerId ? (
                        <>
                          <div className="font-medium text-gray-800 truncate">
                            {visit.ownerId.firstName || visit.ownerId.lastName
                              ? `${visit.ownerId.firstName || ''} ${visit.ownerId.lastName || ''}`.trim()
                              : (visit.ownerId.fullName || visit.ownerId.email || "N/A")}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {visit.ownerId.userId || "-"}
                          </div>
                        </>
                      ) : (
                        <div className="font-medium text-gray-600 text-xs truncate">
                          {visit.ownerId || "Not Assigned"}
                        </div>
                      )}
                    </div>
                  </td>

                  <td className="py-3 px-4">
                    <div className="flex justify-center">
                      {visit?.isPriorityFollowUp ? (
                        <BsFlagFill
                          className="text-red-600 text-lg"
                          title="Priority Follow-up"
                        />
                      ) : (
                        <BsFlag
                          className="text-gray-300 text-lg"
                          title="Normal Follow-up"
                        />
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-xs text-gray-500">
                      {visit.userId?.role || "N/A"}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-medium whitespace-nowrap">
                   <div>
                    <Link
                      to={`/${portalName}/sales/leads/view/${visit._id}`}
                      state={{
                        allIds: filteredData.map((i) => i._id),
                      }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {visit.clientId}
                    </Link>
                   </div>
                    <div className="text-xs text-gray-400">
                      {visit?.businessVerticalId?.name || "-"}
                    </div>
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    <div>
                      {visit.divisionId?.name || "-"}
                    </div>
                    <div className="text-xs text-gray-400">{visit.divisionBaseId || "-"}</div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="min-w-[150px]">
                      <div className="font-semibold truncate">
                        {visit.clientName}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {visit.contactName}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {visit.contactPhone || visit.contactLandline || "-"} 
                      </div>
                    </div>
                  </td>
                  {/* <td className="py-3 px-4">
                    <div className="min-w-[140px]">
                      <div className="font-medium truncate">
                        {visit.industryType}
                      </div>
                      {visit.industrySubType && (
                        <div className="text-xs text-gray-500 truncate">
                          {visit.industrySubType}
                        </div>
                      )}
                    </div>
                  </td> */}
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-center">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1 min-w-[100px] justify-end">
                          <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" />
                          <span className="text-sm font-medium text-green-700 whitespace-nowrap">
                            {formatCurrency(
                              visit.expectedValue,
                              visit.currency,
                            )}
                          </span>
                        </div>

                        <span className="text-gray-400 mx-1">|</span>

                        <div className="flex items-center gap-1 min-w-[100px] justify-start">
                          <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                          <span className="text-sm font-medium text-blue-700 whitespace-nowrap">
                            {formatCurrency(
                              visit.estimatedValue,
                              visit.currency,
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className="px-3 py-1.5 inline-flex text-xs font-medium rounded-full whitespace-nowrap"
                      style={{
                        backgroundColor: visit.stageId?.color
                          ? `${visit.stageId.color}20`
                          : "#E5E7EB",
                        color: visit.stageId?.color || "#6B7280",
                      }}
                    >
                      {visit.stageId?.stageName || "Not Set"}
                    </span>
                  </td>

                  <td className="py-3 px-4 font-semibold whitespace-nowrap">
                    <span
                      className={`px-3 py-1.5 inline-flex text-xs font-medium rounded-full ${getPaymentStatusColor(visit.paymentStatus)}`}
                    >
                      {visit.paymentStatus === "na"
                        ? "N/A"
                        : visit.paymentStatus.charAt(0).toUpperCase() +
                          visit.paymentStatus.slice(1)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-600 whitespace-nowrap">
                    {formatDate(visit.createdAt)}
                  </td>
                  <td className="py-3 px-4 text-gray-600 whitespace-nowrap">
                    {formatDate(visit.lastVisitDate || "N/A")}
                  </td>
                  <td className="py-3 px-4 text-gray-500 rounded-r-lg">
                    <div className="flex items-center justify-center h-full">
                      <div className="relative inline-block text-left">
                        <button
                          onClick={(e) => {
                            const button = e.currentTarget;
                            const buttonRect = button.getBoundingClientRect();
                            setMenuIndex(
                              menuIndex === index
                                ? null
                                : {
                                    index,
                                    position: {
                                      top: buttonRect.bottom + window.scrollY,
                                      left: buttonRect.right - 144,
                                      direction:
                                        window.innerHeight - buttonRect.bottom >
                                        150
                                          ? "below"
                                          : "above",
                                    },
                                  },
                            );
                          }}
                          className="hover:bg-gray-100 rounded-full p-1"
                        >
                          <MoreHorizontal size={20} />
                        </button>

                        {menuIndex?.index === index && (
                          <div
                            ref={dropdownRef}
                            className="fixed z-50 w-36 bg-white shadow-lg rounded-md border border-gray-200"
                            style={{
                              top:
                                menuIndex.position.direction === "below"
                                  ? `${menuIndex.position.top}px`
                                  : `${menuIndex.position.top - 144}px`,
                              left: `${menuIndex.position.left}px`,
                            }}
                          >
                            <ul className="text-sm divide-y divide-gray-200">
                              <li
                                className="px-4 py-2 flex items-center gap-2 hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleView(visit)}
                              >
                                <Eye size={16} /> View
                              </li>
                              <li
                                className="px-4 py-2 flex items-center gap-2 hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleEdit(visit)}
                              >
                                <Edit2 size={16} /> Edit
                              </li>
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {paginatedData.length > 0 && (
        <Pagination
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={(page) => setCurrentPage(page)}
          onItemsPerPageChange={(value) => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}
        />
      )}

      <ExportCard
        isOpen={exportCard.isOpen}
        onClose={() =>
          setExportCard({
            isOpen: false,
            type: null,
            isLoading: false,
            exportProgress: 0,
            status: "idle",
            message: "",
            autoClose: true,
            autoCloseDelay: 3000,
          })
        }
        onExport={() => {
          if (exportCard.status === "error") {
            handleExport(exportCard.type);
          } else {
            if (exportCard.type === "excel") {
              exportToExcel(reportData, null);
              toast.success("Excel downloaded successfully");
            } else {
              exportToPDF(reportData, Logo);
              toast.success("PDF downloaded successfully");
            }
          }
        }}
        isLoading={exportCard.isLoading}
        exportProgress={exportCard.exportProgress}
        status={exportCard.status}
        message={exportCard.message}
        type={exportCard.type}
        autoClose={exportCard.autoClose}
        autoCloseDelay={exportCard.autoCloseDelay}
      />

      {/* Delete Confirmation Offcanvas */}
      <DeleteConfirmationOffcanvas
        isOpen={deleteConfirmation.isOpen}
        onClose={() =>
          setDeleteConfirmation({
            isOpen: false,
            selectedData: null,
            error: null,
          })
        }
        onConfirm={() => handleDelete(deleteConfirmation.selectedData?._id)}
        title="Delete Client Visit"
        itemName={`${deleteConfirmation.selectedData?.clientName} (${deleteConfirmation.selectedData?.clientId})`}
        error={deleteConfirmation.error}
      />
    </div>
  );
};

export default ClientVisits;